import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { RadioButtonModule } from 'primeng/radiobutton';
import { DmInputTextLibComponent } from '@everymatrix-base-components/dm-input-text-lib';
import { DmSelectLibComponent } from '@everymatrix-base-components/dm-select-lib';
import { DmButtonLibComponent } from '@everymatrix-base-components/dm-button-lib';
import { ProgressSpinner } from 'primeng/progressspinner';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DmDatePickerLibComponent } from '@everymatrix-base-components/dm-date-picker-lib';
import {
  DAY_OF_MONTH_OPTIONS,
  DAY_OF_WEEK_OPTIONS,
  GlobalMessageService,
  Lookups,
  REPORTS,
  ReportStrategyFactory,
  ReportTemplate,
  Schedule,
  SCHEDULE_OPTIONS,
  ScheduleGroup,
  SchedulesData,
  TemplateService,
  TIME_OPTIONS,
} from '@everymatrix-base-components/utils';
import { SendingService } from '@everymatrix-base-components/utils';
import { forkJoin, Observable, of, switchMap, take, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeleteTemplateModalComponent } from '../delete-template-modal/delete-template-modal';

export const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

@Component({
  selector: 'lib-send-settings-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    FormsModule,
    RadioButtonModule,
    ProgressSpinner,
    DmInputTextLibComponent,
    DmSelectLibComponent,
    DmButtonLibComponent,
    DmDatePickerLibComponent,
    DeleteTemplateModalComponent,
  ],
  templateUrl: './send-settings-modal.html',
  styleUrls: ['./send-settings-modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SendSettingsModalComponent implements OnInit {
  visible = model(false);
  showDeleteConfirmation = model(false);
  schedulesLoading = signal(false);
  loading = signal(false);
  settingForms = signal(false);
  formWasChanged = signal(false);
  dateTimeFormatOptions = signal<Lookups[]>([]);
  scheduleItems = signal<Schedule[]>([]);
  selectedTemplate = input.required<ReportTemplate>();
  sendingService = inject(SendingService);
  templateService = inject(TemplateService);
  messageService = inject(GlobalMessageService);
  destroyRef = inject(DestroyRef);
  reportStrategyFactory = inject(ReportStrategyFactory);
  emailHint =
    'If you want to enter more than one email, please use comma separator';
  emailErrorMessage = 'Email address is not valid';
  fileNameHint =
    "* If the filename contains one or more '#' characters anywhere within it, you will be able to choose variables.";
  dateTimeFormatHint =
    '* The format will apply if the filename contains the variable #timestamp.';
  radioButtonControl = new FormControl('once');
  onceForm!: FormGroup;
  recurringForm!: FormGroup;
  destinationOptions = [
    { lookup_value: 'Email', lookup_key: 'email' },
    { lookup_value: 'FTP', lookup_key: 'ftp' },
    { lookup_value: 'SFTP', lookup_key: 'sftp' },
  ];
  scheduleOptions = SCHEDULE_OPTIONS;
  dayOfWeekOptions = DAY_OF_WEEK_OPTIONS;
  dayOfMonthOptions = DAY_OF_MONTH_OPTIONS;
  timeUtcOptions = TIME_OPTIONS;
  hasSchedules = false;
  currentSchedule: Schedule | null = null;
  cachedOnceFormValues: Record<string, any> = {};
  cachedRecurringFormValues: Record<string, any> = {};
  currentReport!: {
    reportId: number;
    reportName: string;
    reportKey: string;
  };
  mailTo = '';
  scheduleId = '';

  constructor(private fb: FormBuilder) {
    effect(() => {
      const template = this.selectedTemplate();
      if (template?.domain_id) {
        this.sendingService
          .getTimeStampOptions(template.domain_id)
          .pipe(take(1))
          .subscribe((res) => {
            this.dateTimeFormatOptions.set(res);
          });
      }
    });
    effect(() => {
      if (!this.visible()) {
        this.settingForms.set(false);
        this.currentSchedule = null;
        this.formWasChanged.set(false);
      }
    });
  }

  ngOnInit() {
    this.hasSchedules = this.selectedTemplate().input_params.send_option;
    this.currentReport = Object.values(REPORTS).find(
      (r) => r.reportId === this.selectedTemplate().report_id
    ) as {
      reportId: number;
      reportName: string;
      reportKey: string;
    };

    if (this.hasSchedules) {
      this.schedulesLoading.set(true);
      this.sendingService
        .getSchedules(
          this.currentReport?.reportKey,
          this.selectedTemplate().template_id,
          this.selectedTemplate().domain_id
        )
        .pipe(take(1))
        .subscribe((data: SchedulesData) => {
          if (!data) {
            this.settingForms.set(true);
          } else {
            const scheduleList = data.map(
              (schedule: ScheduleGroup) => schedule.schedules[0]
            );
            scheduleList.forEach((item: Schedule) => {
              item.send_option_period = item.schedule_interval
                ? this.sendingService.transformToPeriodLookupKey(item)
                : null;
              const { schedule_type, time_option, day_of_week, day_of_month } =
                this.sendingService.convertFromCronToDate(item);
              item.schedule_type = schedule_type;
              item.time_option = time_option;
              item.day_of_week = day_of_week;
              item.day_of_month = day_of_month;
            });
            this.scheduleItems.set(scheduleList);
          }
          this.schedulesLoading.set(false);
        });
    }
    this.buildOnceForm();
    this.buildRecurringForm();
  }

  buildOnceForm() {
    this.onceForm = this.fb.group({
      destination: ['email'],
      email: ['', [Validators.required, Validators.pattern(emailRegex)]],
      emailSubject: [''],
      emailCC: ['', [Validators.pattern(emailRegex)]],
      emailBCC: ['', [Validators.pattern(emailRegex)]],
    });
    this.setDynamicOnceFormControls();
  }

  buildRecurringForm() {
    const date = new Date();

    this.recurringForm = this.fb.group({
      scheduleName: [this.currentReport?.reportName || ''],
      destination: ['email'],
      email: ['', [Validators.required, Validators.pattern(emailRegex)]],
      emailSubject: [''],
      emailCC: ['', [Validators.pattern(emailRegex)]],
      emailBCC: ['', [Validators.pattern(emailRegex)]],
      expirationDate: [new Date(new Date(date).setMonth(date.getMonth() + 6))],
      schedule: ['0'],
      time: ['00:00'],
    });
    this.setDynamicRecurringForm();
  }

  setDynamicOnceFormControls() {
    this.onceForm
      .get('destination')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dest) => {
        const keysToCache = [
          'email',
          'emailSubject',
          'emailCC',
          'emailBCC',
          'connectionId',
          'folderName',
          'fileName',
          'dateTimeFormat',
        ];

        keysToCache.forEach((key) => {
          if (this.onceForm.contains(key)) {
            this.cachedOnceFormValues[key] = this.onceForm.get(key)?.value;
          }
        });

        keysToCache.forEach((name) => {
          if (this.onceForm.contains(name)) {
            this.onceForm.removeControl(name);
          }
        });

        if (dest === 'email') {
          this.onceForm.addControl(
            'email',
            new FormControl(this.cachedOnceFormValues['email'] ?? '', [
              Validators.required,
              Validators.pattern(emailRegex),
            ])
          );
          this.onceForm.addControl(
            'emailSubject',
            new FormControl(this.cachedOnceFormValues['emailSubject'] ?? '')
          );
          this.onceForm.addControl(
            'emailCC',
            new FormControl(this.cachedOnceFormValues['emailCC'] ?? '', [
              Validators.pattern(emailRegex),
            ])
          );
          this.onceForm.addControl(
            'emailBCC',
            new FormControl(this.cachedOnceFormValues['emailBCC'] ?? '', [
              Validators.pattern(emailRegex),
            ])
          );
        } else {
          this.onceForm.addControl(
            'connectionId',
            new FormControl(
              this.cachedOnceFormValues['connectionId'] ?? '',
              Validators.required
            )
          );
          this.onceForm.addControl(
            'folderName',
            new FormControl(
              this.cachedOnceFormValues['folderName'] ?? '',
              Validators.required
            )
          );
          this.onceForm.addControl(
            'fileName',
            new FormControl(this.cachedOnceFormValues['fileName'] ?? '')
          );
          this.onceForm.addControl(
            'dateTimeFormat',
            new FormControl(
              this.cachedOnceFormValues['dateTimeFormat'] ??
                'YYYY-MM-DD_HHMISS_MS'
            )
          );
        }
      });
  }

  setDynamicRecurringForm() {
    const keysToCache = [
      'email',
      'emailSubject',
      'emailCC',
      'emailBCC',
      'connectionId',
      'folderName',
      'fileName',
      'dateTimeFormat',
      'dayOfWeek',
      'dayOfMonth',
    ];

    const cacheValues = () => {
      keysToCache.forEach((key) => {
        if (this.recurringForm.contains(key)) {
          this.cachedRecurringFormValues[key] =
            this.recurringForm.get(key)?.value;
        }
      });
    };

    const restoreEmailControls = () => {
      this.recurringForm.addControl(
        'email',
        new FormControl(this.cachedRecurringFormValues['email'] ?? '', [
          Validators.required,
          Validators.pattern(emailRegex),
        ])
      );
      this.recurringForm.addControl(
        'emailSubject',
        new FormControl(this.cachedRecurringFormValues['emailSubject'] ?? '')
      );
      this.recurringForm.addControl(
        'emailCC',
        new FormControl(this.cachedRecurringFormValues['emailCC'] ?? '', [
          Validators.pattern(emailRegex),
        ])
      );
      this.recurringForm.addControl(
        'emailBCC',
        new FormControl(this.cachedRecurringFormValues['emailBCC'] ?? '', [
          Validators.pattern(emailRegex),
        ])
      );
    };

    const restoreFtpControls = () => {
      this.recurringForm.addControl(
        'connectionId',
        new FormControl(
          this.cachedRecurringFormValues['connectionId'] ?? '',
          Validators.required
        )
      );
      this.recurringForm.addControl(
        'folderName',
        new FormControl(
          this.cachedRecurringFormValues['folderName'] ?? '',
          Validators.required
        )
      );
      this.recurringForm.addControl(
        'fileName',
        new FormControl(this.cachedRecurringFormValues['fileName'] ?? '')
      );
      this.recurringForm.addControl(
        'dateTimeFormat',
        new FormControl(
          this.cachedRecurringFormValues['dateTimeFormat'] ??
            'YYYY-MM-DD_HHMISS_MS'
        )
      );
    };

    const restoreScheduleControls = (value: string) => {
      if (value === '1') {
        this.recurringForm.addControl(
          'dayOfWeek',
          new FormControl(this.cachedRecurringFormValues['dayOfWeek'] ?? '1')
        );
      } else if (value === '2') {
        this.recurringForm.addControl(
          'dayOfMonth',
          new FormControl(this.cachedRecurringFormValues['dayOfMonth'] ?? '1')
        );
      }
    };

    this.recurringForm.get('destination')!.valueChanges.subscribe((dest) => {
      cacheValues();

      const removable = [
        'email',
        'emailSubject',
        'emailCC',
        'emailBCC',
        'connectionId',
        'folderName',
        'fileName',
        'dateTimeFormat',
      ];
      removable.forEach((name) => {
        if (this.recurringForm.contains(name)) {
          this.recurringForm.removeControl(name);
        }
      });

      if (dest === 'email') {
        restoreEmailControls();
      } else {
        restoreFtpControls();
      }
    });

    this.recurringForm.get('schedule')!.valueChanges.subscribe((val) => {
      cacheValues();

      ['dayOfWeek', 'dayOfMonth'].forEach((name) => {
        if (this.recurringForm.contains(name)) {
          this.recurringForm.removeControl(name);
        }
      });

      restoreScheduleControls(val);
    });

    restoreScheduleControls(this.recurringForm.get('schedule')?.value);
  }

  onShow() {
    this.recurringForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.formWasChanged.set(true));
  }

  editSchedule(schedule: Schedule) {
    const params: any = {
      scheduleName: schedule.schedule_name,
      destination: schedule.connection_type,
      email: schedule.connection_details.email_to,
      emailSubject: schedule.connection_details.email_subject,
      emailCC: schedule.connection_details.email_cc,
      emailBCC: schedule.connection_details.email_bcc,
      schedule: schedule.send_option_period,
      time: schedule.time_option,
      expirationDate: new Date(schedule.expiration_date),
    };
    if (params.schedule === '1') {
      params.dayOfWeek = schedule.day_of_week;
    } else if (params.schedule === '2') {
      params.dayOfMonth = schedule.day_of_month;
    }

    this.recurringForm.patchValue(params);
    this.onceForm.get('email')?.patchValue(params.email);
    this.radioButtonControl.setValue('recurring');
    this.settingForms.set(true);
    this.currentSchedule = schedule;
  }

  deleteSchedule() {
    this.loading.set(true);

    this.sendingService
      .deleteSchedule(this.selectedTemplate().domain_id, this.scheduleId)
      .pipe(
        take(1),
        switchMap((schedule: any) => {
          const remainingItems = this.scheduleItems().filter(
            (t) => t.schedule_id !== schedule.schedule_id
          );

          if (!remainingItems.length) {
            return this.saveTemplate(false, this.mailTo).pipe(
              tap((template) => {
                this.templateService.templatesList.update(
                  (templates: ReportTemplate[] | undefined) =>
                    (templates as ReportTemplate[]).map((item) =>
                      item.template_id === template.template_id
                        ? template
                        : item
                    )
                );
              })
            );
          }
          return of(null);
        })
      )
      .subscribe({
        next: () => {
          this.messageService.showSuccessMessage({
            detail: 'Schedule has been deleted successfully',
          });
          this.visible.set(false);
          this.loading.set(false);
          this.showDeleteConfirmation.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.showDeleteConfirmation.set(false);
        },
      });
  }

  sendNow() {
    this.loading.set(true);

    this.triggerAirFlow()
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.messageService.showSuccessMessage({
            detail: 'The report has been sent successfully',
          });
          this.visible.set(false);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  private triggerAirFlow(): Observable<any> {
    const request = this.sendingService.getRequestParamsToAirflow(
      this.selectedTemplate(),
      this.radioButtonControl.value === 'once'
        ? this.onceForm.value
        : this.recurringForm.value,
      this.getReportBodyModel()
    );

    return this.sendingService.triggerAirflowJob(
      this.selectedTemplate(),
      request.p_report_params,
      this.radioButtonControl.value === 'once'
    );
  }

  saveSchedule() {
    const scheduleInterval = {
      schedulePeriod:
        this.recurringForm.value.schedule || this.scheduleOptions[0].lookup_key,
      month:
        this.recurringForm.value.dayOfMonth ||
        DAY_OF_MONTH_OPTIONS[0].lookup_key,
      weekDay:
        this.recurringForm.value.dayOfWeek || DAY_OF_WEEK_OPTIONS[0].lookup_key,
      timeHours: this.recurringForm.value.time || TIME_OPTIONS[0].lookup_key,
    };

    this.loading.set(true);

    this.sendingService
      .saveReportSchedule(
        this.selectedTemplate(),
        this.recurringForm.value,
        this.getReportBodyModel(),
        scheduleInterval,
        this.currentSchedule
      )
      .pipe(
        switchMap((x) => {
          const saveTemplate$ = this.saveTemplate().pipe(
            take(1),
            tap((template) => {
              this.templateService.templatesList.update(
                (templates: ReportTemplate[] | undefined) =>
                  (templates as ReportTemplate[]).map((item) =>
                    item.template_id === template.template_id ? template : item
                  )
              );
            })
          );

          const triggerAirflow$ = this.triggerAirFlow().pipe(take(1));

          return forkJoin([saveTemplate$, triggerAirflow$]);
        }),
        take(1)
      )
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.visible.set(false);
          this.messageService.showSuccessMessage({
            detail: 'The report has been saved and sent successfully',
          });
        },
        error: () => this.loading.set(false),
      });
  }

  saveTemplate(sendOption = true, email?: string) {
    const {
      template_name,
      template_desc,
      report_id,
      template_id,
      input_params,
    } = this.selectedTemplate();
    const params = {
      ...input_params,
      send_option: sendOption,
    };

    if (email) {
      params.userEmail = email;
    } else {
      if (this.onceForm.value.email || this.recurringForm.value.email) {
        params.userEmail =
          this.onceForm.value.email || this.recurringForm.value.email;
      }
    }

    return this.templateService.saveTemplate(
      template_name,
      template_desc,
      params,
      report_id,
      template_id,
      false
    );
  }

  newSending() {
    this.settingForms.set(true);
  }

  getReportBodyModel() {
    //TODO: to avoid using if or switch create a bridge for all reports
    if (this.currentReport.reportId === 100) {
      const config = {
        offset: null,
        limit: null,
        allFilters: this.selectedTemplate().input_params.allFields,
        formData: this.selectedTemplate().input_params.fieldValues,
        columns: this.selectedTemplate().input_params.visibleFields,
        executionType: 'csv_load',
        sorting: { field: null, value: null },
        template: this.selectedTemplate(),
      };
      return this.reportStrategyFactory
        .getStrategy(this.currentReport.reportKey)
        .buildRequestBody(config);
    } else {
      return {};
    }
  }
}
